import { toMove } from '@tentactoe/engine';
import type { GameState, Index, Move } from '@tentactoe/engine';
import type { Theme } from '../theme';
import { Mark } from './Mark';

interface MiniBoardProps {
  readonly state: GameState;
  readonly board: Index;
  readonly theme: Theme;
  readonly onPlay: (move: Move) => void;
  /** True while it is not the local player's turn to act. */
  readonly locked: boolean;
}

function frameClass(state: GameState, board: Index, theme: Theme): string {
  const { classes } = theme;
  const result = state.miniResults[board];

  if (result === 'X') return classes.miniWonX;
  if (result === 'O') return classes.miniWonO;
  if (result === 'draw') return classes.miniDrawn;

  const forced = state.activeBoard === null || state.activeBoard === board;
  return forced && state.result === null ? classes.miniActive : classes.miniIdle;
}

export function MiniBoard({ state, board, theme, onPlay, locked }: MiniBoardProps) {
  const { classes } = theme;
  const result = state.miniResults[board];
  const open =
    !locked &&
    state.result === null &&
    result === null &&
    (state.activeBoard === null || state.activeBoard === board);

  return (
    <div
      className={`grid grid-cols-3 border-2 p-1 w-fit relative rounded ${frameClass(state, board, theme)}`}
    >
      {state.boards[board].map((cell, index) => {
        const playable = open && cell === null;
        const move = toMove(board, index);

        return (
          <button
            key={index}
            type="button"
            disabled={!playable}
            onClick={() => onPlay(move)}
            aria-label={`Board ${board + 1}, cell ${index + 1}${cell ? `, ${cell}` : ''}`}
            className={`${classes.cellBase} ${
              result !== null
                ? classes.cellResolved
                : playable
                  ? classes.cellPlayable
                  : classes.cellBlocked
            }`}
          >
            <Mark value={cell} theme={theme} />
          </button>
        );
      })}

      {result !== null && (
        <div className={classes.miniOverlay}>
          <Mark value={result} theme={theme} className="w-16 h-16 sm:w-20 sm:h-20" />
        </div>
      )}
    </div>
  );
}
